import React, { useEffect, useState } from "react";
import type { DemoLead } from "./demoOps.lib";
import { leadDisplayName } from "./demoOps.lib";
import ClickToCall from "../../../Telephony/ClickToCall";
import { Badge, EmptyState, Table } from "../../../ui";
import { DemoStatusBadge } from "./DemoStatusBadge";
import {
  aeDisplayName,
  formatAeJoined,
  formatDemoStartTime,
  formatStudentJoined,
  getLiveDemoStatusBadge,
  isDomesticIndiaLead,
  type LiveDemoSortDir,
} from "./liveDemoUtils";

interface LiveDemoTableProps {
  leads: DemoLead[];
  onRowOpenDetail: (lead: DemoLead) => void;
  sortDir?: LiveDemoSortDir;
  onToggleSort?: () => void;
}

function LiveStatusCell({ lead }: { lead: DemoLead }) {
  const badge = getLiveDemoStatusBadge(lead);
  return (
    <div className="ds-stack demo-ops-status-cell">
      <DemoStatusBadge status={badge.label} />
      {badge.elapsed ? <span className="ds-text-subtle ds-num">{badge.elapsed}</span> : null}
    </div>
  );
}

function AeJoinedCell({ lead }: { lead: DemoLead }) {
  const joined = formatAeJoined(lead);
  if (joined.kind === "not_found") return <Badge tone="danger">AE not found</Badge>;
  if (joined.kind === "future") return <span>—</span>;
  if (joined.kind === "not_joined") return <span>AE has not joined</span>;
  if (joined.kind === "late") {
    return (
      <div className="ds-stack demo-ops-status-cell">
        <span>{joined.time}</span>
        <Badge tone="danger">AE joined late</Badge>
      </div>
    );
  }
  return <span>{joined.time}</span>;
}

export const LiveDemoTable: React.FC<LiveDemoTableProps> = ({
  leads,
  onRowOpenDetail,
  sortDir = "desc",
  onToggleSort,
}) => {
  const [, setNowTs] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNowTs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className="demo-ops-scroll">
      <Table className="demo-ops-live" caption="Live demos">
        <thead>
          <tr>
            <th aria-sort={sortDir === "asc" ? "ascending" : "descending"}>
              <button
                type="button"
                onClick={onToggleSort}
                style={{ all: "inherit", cursor: "pointer" }}
                title="Sort by demo time"
              >
                Demo Time {sortDir === "asc" ? "▲" : "▼"}
              </button>
            </th>
            <th>Demo Status</th>
            <th>Student Name</th>
            <th>Age Group</th>
            <th>Student Joined</th>
            <th>AE Name</th>
            <th>AE Joined</th>
            <th>Timezone</th>
            <th>Course Name</th>
            <th>Contact</th>
            <th>User Id</th>
          </tr>
        </thead>
        <tbody>
          {leads.length === 0 ? (
            <tr>
              <td colSpan={11}>
                <EmptyState
                  title="No live demos"
                  description="Nothing matches right now. Clear the filters or wait for the next refresh (every 5 seconds)."
                />
              </td>
            </tr>
          ) : (
            leads.map((lead, index) => {
              const highlight = isDomesticIndiaLead(lead);
              return (
                <tr
                  key={`${lead.classScheduleId ?? lead.userId ?? lead.studentId ?? index}-${lead.startTime ?? index}`}
                  className={`demo-ops-row-clickable ${highlight ? "domestic-call-row" : ""}`.trim()}
                  onClick={() => onRowOpenDetail(lead)}
                >
                  <td>{formatDemoStartTime(lead.startTime)}</td>
                  <td>
                    <LiveStatusCell lead={lead} />
                  </td>
                  <td>{leadDisplayName(lead)}</td>
                  <td>{lead.ageGroup ?? "—"}</td>
                  <td>{formatStudentJoined(lead)}</td>
                  <td>{aeDisplayName(lead)}</td>
                  <td>
                    <AeJoinedCell lead={lead} />
                  </td>
                  <td>{lead.studentTimezone ?? "—"}</td>
                  <td>{lead.courseName ?? "—"}</td>
                  <td>
                    <ClickToCall
                      number={
                        lead.mobileNumber
                          ? `${lead.countryCode ?? ""}${lead.mobileNumber}`
                          : undefined
                      }
                      name={leadDisplayName(lead)}
                      sourceRecordId={
                        lead.recordId != null ? String(lead.recordId) : undefined
                      }
                      customerUserId={
                        lead.userId != null ? String(lead.userId) : undefined
                      }
                    />
                  </td>
                  <td>{lead.userId ?? "—"}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default LiveDemoTable;
